import { PERMISSIONS } from '@/configs/permissions';
import type { RootState } from '@/redux-store';
import { store } from '@/redux-store';
import type { RouteManager } from '@/utils/route/routeManager';
import type { ActionTableKeys, ActionTablePermissions } from './types';

export class PermissionManager {
    public RouteManager: RouteManager;

    public constructor(RouteManager: RouteManager) {
        this.RouteManager = RouteManager;
    }

    public hasPermission(
        routePermissions: string[],
        userPermissions: string[]
    ) {
        return routePermissions.some(p => {
            if (routePermissions.includes('*')) {
                return true;
            }

            const regex = new RegExp(`^${p}$`);

            return userPermissions.some(up => regex.test(up));
        });
    }

    public getPermissionTableUtils(page: string) {
        const P = PERMISSIONS;

        const pageTablePermissions: ActionTablePermissions = {
            [this.RouteManager.TASK]: {
                addNew: { permissions: [P.TaskCheck.Create] },
                update: { permissions: [P.TaskCheck.Update] },
                delete: { permissions: [P.TaskCheck.Delete] },
                detail: { permissions: [P.TaskCheck.GetDetail] },
                export: { permissions: [P.TaskCheck.Export] },
                import: { permissions: ['*'] },
                filter: { permissions: ['*'] },
            },
            [this.RouteManager.TICKET]: {
                addNew: { permissions: [P.Ticket.Create] },
                update: { permissions: [P.Ticket.Update] },
                delete: { permissions: [P.Ticket.Delete] },
                detail: { permissions: [P.Ticket.GetDetail] },
                export: { permissions: [P.Ticket.Export] },
                import: { permissions: ['*'] },
                filter: { permissions: ['*'] },
            },
            [this.RouteManager.BUSINESS_TRIP_FORM]: {
                addNew: { permissions: [P.BusinessTripForm.Create] },
                update: { permissions: [P.BusinessTripForm.Update] },
                delete: { permissions: [P.BusinessTripForm.Delete] },
                detail: { permissions: [P.BusinessTripForm.GetDetail] },
                export: { permissions: [P.BusinessTripForm.Export] },
                import: { permissions: ['*'] },
                filter: { permissions: ['*'] },
            },
            [this.RouteManager.DEVICE]: {
                addNew: { permissions: [P.Device.Create] },
                update: { permissions: [P.Device.Update] },
                delete: { permissions: [P.Device.Delete] },
                detail: { permissions: [P.Device.GetDetail] },
                export: { permissions: [P.Device.Export] },
                import: { permissions: [P.Device.Import] },
                filter: { permissions: ['*'] },
            },
            [this.RouteManager.ERROR_DETAIL_TASK_CHECK]: {
                addNew: { permissions: [P.ErrorDetailTaskCheck.Create] },
                update: { permissions: [P.ErrorDetailTaskCheck.Update] },
                delete: { permissions: [P.ErrorDetailTaskCheck.Delete] },
                detail: { permissions: [P.ErrorDetailTaskCheck.GetDetail] },
                export: { permissions: ['*'] },
                import: { permissions: ['*'] },
                filter: { permissions: ['*'] },
            },
            [this.RouteManager.QUALITY_MANAGEMENT]: {
                addNew: { permissions: [P.QualityManagement.Create] },
                update: { permissions: [P.QualityManagement.Update] },
                delete: { permissions: [P.QualityManagement.Delete] },
                detail: { permissions: [P.QualityManagement.GetDetail] },
                export: { permissions: [P.QualityManagement.Export] },
                import: { permissions: ['*'] },
                filter: { permissions: ['*'] },
            },
            [this.RouteManager.COMPONENT_RECOMMENDATION]: {
                addNew: { permissions: ['*'] },
                update: { permissions: [P.PartRequisition.Update] },
                delete: { permissions: [P.PartRequisition.Delete] },
                detail: { permissions: [P.PartRequisition.GetDetail] },
                export: { permissions: ['*'] },
                import: { permissions: ['*'] },
                filter: { permissions: ['*'] },
            },
            [this.RouteManager.STAFF]: {
                addNew: { permissions: [P.Admin.CreateUser] },
                update: { permissions: [P.Admin.ResetPassword] },
                delete: { permissions: [P.Admin.DeleteUser] },
                detail: { permissions: [P.Admin.GetUserDetail] },
                export: { permissions: [P.Admin.Export] },
                import: { permissions: [P.Admin.Import] },
                filter: { permissions: ['*'] },
            },
            [this.RouteManager.CUSTOMER]: {
                addNew: { permissions: [P.Customer.Create] },
                update: { permissions: [P.Customer.Update] },
                delete: { permissions: [P.Customer.Delete] },
                detail: { permissions: [P.Customer.GetDetail] },
                export: { permissions: [P.Customer.Export] },
                import: { permissions: [P.Customer.Import] },
                filter: { permissions: ['*'] },
            },
            [this.RouteManager.ROLE]: {
                addNew: { permissions: [P.AppRole.Create] },
                update: { permissions: [P.AppRole.Update] },
                delete: { permissions: [P.AppRole.Delete] },
                detail: { permissions: [P.Admin.GetPermissionRole] },
                export: { permissions: ['*'] },
                import: { permissions: ['*'] },
                filter: { permissions: ['*'] },
            },
            [this.RouteManager.COORDINATION]: {
                addNew: { permissions: [P.StaffCoordination.Create] },
                update: { permissions: ['*'] },
                import: { permissions: ['*'] },
                export: { permissions: ['*'] },
                delete: { permissions: ['*'] },
                filter: { permissions: ['*'] },
                detail: { permissions: ['*'] },
            },
            [this.RouteManager.VIOLATION_CUSTOMER]: {
                addNew: { permissions: [P.ViolationDetailTaskCheck.Create] },
                update: { permissions: [P.ViolationDetailTaskCheck.Update] },
                delete: { permissions: [P.ViolationDetailTaskCheck.Delete] },
                export: { permissions: [P.ViolationDetailTaskCheck.Export] },
                import: { permissions: ['*'] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.ViolationDetailTaskCheck.GetDetail],
                },
            },
            [this.RouteManager.DEVICE_TYPE]: {
                addNew: { permissions: [P.DeviceType.Create] },
                update: { permissions: [P.DeviceType.Update] },
                delete: { permissions: [P.DeviceType.Delete] },
                export: { permissions: [P.DeviceType.Export] },
                import: { permissions: [P.DeviceType.Import] },
                filter: { permissions: ['*'] },
                detail: { permissions: [P.DeviceType.GetDetail] },
            },
            [this.RouteManager.DEVICE_GROUP]: {
                addNew: { permissions: [P.DeviceGroup.Create] },
                update: { permissions: [P.DeviceGroup.Update] },
                delete: { permissions: [P.DeviceGroup.Delete] },
                export: { permissions: [P.DeviceGroup.Export] },
                import: { permissions: [P.DeviceGroup.Import] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.DeviceGroup.GetDetail],
                },
            },
            [this.RouteManager.DEVICE_SKU]: {
                addNew: { permissions: [P.DeviceSKU.Create] },
                update: { permissions: [P.DeviceSKU.Update] },
                delete: { permissions: [P.DeviceSKU.Delete] },
                export: { permissions: [P.DeviceSKU.Export] },
                import: { permissions: [P.DeviceSKU.Import] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.DeviceSKU.GetDetail],
                },
            },
            [this.RouteManager.DEVICE_MODEL]: {
                addNew: { permissions: [P.DeviceModel.Create] },
                update: { permissions: [P.DeviceModel.Update] },
                delete: { permissions: [P.DeviceModel.Delete] },
                import: { permissions: [P.DeviceModel.Import] },
                export: { permissions: [P.DeviceModel.Export] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.DeviceModel.GetDetail],
                },
            },
            [this.RouteManager.COMPONENT_CATEGORY]: {
                addNew: { permissions: [P.PartCategory.Create] },
                update: { permissions: [P.PartCategory.Update] },
                delete: { permissions: [P.PartCategory.Delete] },
                export: { permissions: [P.PartCategory.Export] },
                import: { permissions: [P.PartCategory.Import] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.PartCategory.GetDetail],
                },
            },
            [this.RouteManager.COMPONENT_TYPE]: {
                addNew: { permissions: [P.PartType.Create] },
                update: { permissions: [P.PartType.Update] },
                delete: { permissions: [P.PartType.Delete] },
                export: { permissions: ['*'] },
                import: { permissions: [P.PartType.Import] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.PartType.GetDetail],
                },
            },
            [this.RouteManager.COMPONENT_SKU]: {
                addNew: { permissions: [P.PartSKU.Create] },
                update: { permissions: [P.PartSKU.Update] },
                delete: { permissions: [P.PartSKU.Delete] },
                export: { permissions: ['*'] },
                import: { permissions: [P.PartSKU.Import] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.PartSKU.GetDetail],
                },
            },
            [this.RouteManager.TICKET_TYPE]: {
                addNew: { permissions: [P.TicketType.Create] },
                update: { permissions: [P.TicketType.Update] },
                delete: { permissions: [P.TicketType.Delete] },
                export: { permissions: ['*'] },
                import: { permissions: ['*'] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.TicketType.GetDetail],
                },
            },
            [this.RouteManager.TASK_TYPE]: {
                addNew: { permissions: [P.TaskCheckType.Create] },
                update: { permissions: [P.TaskCheckType.Update] },
                delete: { permissions: [P.TaskCheckType.Delete] },
                export: { permissions: ['*'] },
                import: { permissions: ['*'] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.TaskCheckType.GetDetail],
                },
            },

            //
            [this.RouteManager.USAGE_TYPE]: {
                addNew: { permissions: [P.UsageType.Create] },
                update: { permissions: [P.UsageType.Update] },
                import: { permissions: ['*'] },
                export: { permissions: ['*'] },
                delete: { permissions: [P.UsageType.Delete] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.UsageType.GetDetail],
                },
            },
            [this.RouteManager.ERROR_TYPE]: {
                addNew: { permissions: [P.ErrorType.Create] },
                update: { permissions: [P.ErrorType.Update] },
                import: { permissions: [P.ErrorType.Import] },
                export: { permissions: [P.ErrorType.Export] },
                delete: { permissions: [P.ErrorType.Delete] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.ErrorType.GetDetail],
                },
            },
            [this.RouteManager.ERROR]: {
                addNew: { permissions: [P.ErrorDetail.Create] },
                update: { permissions: [P.ErrorDetail.Update] },
                import: { permissions: [P.ErrorDetail.Import] },
                export: { permissions: [P.ErrorDetail.Export] },
                delete: { permissions: [P.ErrorDetail.Delete] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.ErrorDetail.GetDetail],
                },
            },
            [this.RouteManager.VIOLATION_TYPE]: {
                addNew: { permissions: [P.ViolationType.Create] },
                update: { permissions: [P.ViolationType.Update] },
                import: { permissions: ['*'] },
                export: { permissions: ['*'] },
                delete: { permissions: [P.ViolationType.Delete] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.ViolationType.GetDetail],
                },
            },
            [this.RouteManager.VIOLATION]: {
                addNew: { permissions: [P.ViolationDetail.Create] },
                update: { permissions: [P.ViolationDetail.Update] },
                import: { permissions: [P.ViolationDetail.Import] },
                export: { permissions: [P.ViolationDetail.Export] },
                delete: { permissions: [P.ViolationDetail.Delete] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.ViolationDetail.GetDetail],
                },
            },
            [this.RouteManager.COMPONENT_GROUP]: {
                addNew: { permissions: [P.PartGroup.Create] },
                update: { permissions: [P.PartGroup.Update] },
                import: { permissions: [P.PartGroup.Import] },
                export: { permissions: ['*'] },
                delete: { permissions: [P.PartGroup.Delete] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.PartGroup.GetDetail],
                },
            },
            [this.RouteManager.TEMPLATE_CHECK]: {
                addNew: { permissions: [P.TemplateCheck.Create] },
                update: { permissions: [P.TemplateCheck.Update] },
                import: { permissions: ['*'] },
                export: { permissions: ['*'] },
                delete: { permissions: [P.TemplateCheck.Delete] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.TemplateCheck.GetDetail],
                },
            },
            [this.RouteManager.VIOLATION_DETAIL]: {
                addNew: { permissions: [P.ViolationDetail.Create] },
                update: { permissions: [P.ViolationDetail.Update] },
                import: { permissions: [P.ViolationDetail.Import] },
                export: { permissions: [P.ViolationDetail.Export] },
                delete: { permissions: [P.ViolationDetail.Delete] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.ViolationDetail.GetDetail],
                },
            },
            [this.RouteManager.COMPONENT_DETAIL]: {
                addNew: { permissions: [P.PartDetail.Create] },
                update: { permissions: [P.PartDetail.Update] },
                import: { permissions: ['*'] },
                export: { permissions: ['*'] },
                delete: { permissions: [P.PartDetail.Delete] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.PartDetail.GetDetail],
                },
            },
            [this.RouteManager.REMINDER_SCHEDULE_NOTIFICATION]: {
                addNew: { permissions: [] },
                update: {
                    permissions: [P.ReminderScheduleNotification.Update],
                },
                import: { permissions: ['*'] },
                export: { permissions: ['*'] },
                delete: { permissions: ['*'] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.ReminderScheduleNotification.GetList],
                },
            },
            [this.RouteManager.ATTRIBUTE_DEVICE_GROUP]: {
                addNew: { permissions: [P.AttributeDeviceGroup.Create] },
                update: {
                    permissions: [],
                },
                import: { permissions: [P.AttributeDeviceGroup.Import] },
                export: { permissions: ['*'] },
                delete: { permissions: [P.AttributeDeviceGroup.Delete] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.AttributeDeviceGroup.GetList],
                },
            },
            [this.RouteManager.ATTRIBUTE_DEVICE_VALUE]: {
                addNew: { permissions: [P.AttributeDeviceValue.Create] },
                update: {
                    permissions: [P.AttributeDeviceValue.Update],
                },
                import: { permissions: ['*'] },
                export: { permissions: ['*'] },
                delete: { permissions: [P.AttributeDeviceValue.Delete] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.AttributeDeviceValue.GetList],
                },
            },
            [this.RouteManager.CUSTOMER_CONTACT]: {
                addNew: { permissions: [P.CustomerContact.Create] },
                update: {
                    permissions: [P.CustomerContact.Update],
                },
                import: { permissions: ['*'] },
                export: { permissions: ['*'] },
                delete: { permissions: [P.CustomerContact.Delete] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.CustomerContact.GetDetail],
                },
            },
            [this.RouteManager.CUSTOMER_REVIEW]: {
                addNew: { permissions: [P.CustomerReview.Create] },
                update: {
                    permissions: [P.CustomerReview.Update],
                },
                import: { permissions: ['*'] },
                export: { permissions: ['*'] },
                delete: { permissions: [] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.CustomerReview.GetDetail],
                },
            },
            [this.RouteManager.PART_INSTALL]: {
                addNew: { permissions: [P.PartInstall.Create] },
                update: {
                    permissions: [P.PartInstall.Update],
                },
                import: { permissions: ['*'] },
                export: { permissions: ['*'] },
                delete: { permissions: [P.PartInstall.Delete] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.PartInstall.GetDetail],
                },
            },
            [this.RouteManager.PART_RECEIVE]: {
                addNew: { permissions: [P.PartReceive.Create] },
                update: {
                    permissions: [P.PartReceive.Update],
                },
                import: { permissions: ['*'] },
                export: { permissions: ['*'] },
                delete: { permissions: [P.PartReceive.Delete] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.PartReceive.GetDetail],
                },
            },
            [this.RouteManager.PART_REPLACE]: {
                addNew: { permissions: [P.PartReplace.Create] },
                update: {
                    permissions: [P.PartReplace.Update],
                },
                import: { permissions: ['*'] },
                export: { permissions: ['*'] },
                delete: { permissions: [P.PartReplace.Delete] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.PartReplace.GetDetail],
                },
            },
            [this.RouteManager.TECHNICIAN_LOG]: {
                addNew: { permissions: [P.TechnicianLog.Create] },
                update: {
                    permissions: [P.TechnicianLog.Update],
                },
                import: { permissions: ['*'] },
                export: { permissions: ['*'] },
                delete: { permissions: [P.TechnicianLog.Delete] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.TechnicianLog.GetDetail],
                },
            },
            [this.RouteManager.REMINDER_SCHEDULE]: {
                addNew: { permissions: [P.ReminderSchedule.Create] },
                update: {
                    permissions: [P.ReminderSchedule.Update],
                },
                import: { permissions: ['*'] },
                export: { permissions: [] },
                delete: { permissions: [P.ReminderSchedule.Delete] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.ReminderSchedule.GetList],
                },
            },
            [this.RouteManager.ADMIN]: {
                addNew: { permissions: [P.Admin.CreateUser] },
                update: {
                    permissions: [P.Admin.UpdateUser],
                },
                import: { permissions: [P.Admin.Import] },
                export: { permissions: [P.Admin.Export] },
                delete: { permissions: [P.Admin.DeleteUser] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [P.Admin.GetUserDetail],
                },
            },
            [this.RouteManager.CUSTOMER_ACCOUNT]: {
                addNew: { permissions: [P.CustomerAccount.Create] },
                update: {
                    permissions: [P.CustomerAccount.Update],
                },
                import: { permissions: ['*'] },
                export: { permissions: ['*'] },
                delete: { permissions: [P.CustomerAccount.Delete] },
                filter: { permissions: ['*'] },
                detail: {
                    permissions: [],
                },
            },
            [this.RouteManager.MULTI_LANGUAGE_CONFIG]: {
                addNew: { permissions: [P.Translation.Create] },
                update: {
                    permissions: [P.Translation.Update],
                },
                import: { permissions: [P.Translation.Import] },
                export: { permissions: [P.Translation.Export] },
                delete: { permissions: [P.Translation.Delete] },
                filter: { permissions: ['*'] },
            },
            [this.RouteManager.DYNAMIC_TICKET]: {
                addNew: { permissions: [P.TicketTemplate.Create] },
                update: {
                    permissions: [P.TicketTemplate.Update],
                },
                import: { permissions: ['*'] },
                export: { permissions: ['*'] },
                delete: { permissions: [P.TicketTemplate.Delete] },
                filter: { permissions: ['*'] },
                detail: { permissions: [P.TicketTemplate.GetDetail] },
            },
            [this.RouteManager.DYNAMIC_TASK]: {
                addNew: { permissions: [P.TaskTemplateDevice.Create] },
                update: {
                    permissions: [P.TaskTemplateDevice.Update],
                },
                import: { permissions: ['*'] },
                export: { permissions: ['*'] },
                delete: { permissions: [P.TaskTemplateDevice.Delete] },
                filter: { permissions: ['*'] },
                detail: { permissions: [P.TaskTemplateDevice.GetDetail] },
            },
        };

        if (!pageTablePermissions[page]) {
            return {};
        }

        return pageTablePermissions[page];
    }

    public getActionPermissionTable(
        page: string
    ): Record<ActionTableKeys, boolean> {
        const state = store.getState() as RootState;
        const userPermissions = state.auth.user?.permissions || [];

        const tablePermissions = this.getPermissionTableUtils(page);

        const result: Record<string, boolean> = {};

        for (const actionKey in tablePermissions) {
            const action = tablePermissions[actionKey];
            const requiredPermissions = action.permissions || [];

            // '*' means all users are allowed
            if (requiredPermissions.includes('*')) {
                result[actionKey] = true;
            } else {
                // Check if user has at least one of the required permissions
                const hasPermission = requiredPermissions.some(perm =>
                    userPermissions.includes(perm)
                );

                result[actionKey] = hasPermission;
            }
        }

        return result;
    }

    public hasUserPermission(permissions: string[]): boolean {
        if (permissions.includes('*')) {
            return true;
        }

        const state = store.getState() as RootState;
        const userPermissions = state.auth.user?.permissions ?? [];

        return permissions.some(p => userPermissions.includes(p));
    }
}
